import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { strings } from '../strings';
import { Header } from '../components/Header';
import { AccountMenu } from '../components/AccountMenu';
import { Rule, Exception } from '../engine/timeEngine';

interface UpcomingItem {
  date: string;
  day: string;
  start_time: string;
  end_time: string;
  status: 'usual' | 'today' | 'changed' | 'cancelled' | 'extra';
  tagText?: string;
  ruleId?: number | null;
  exceptionId?: number | null;
}

const WEEKDAYS = [
  { key: 1, label: 'Mon' },
  { key: 2, label: 'Tue' },
  { key: 3, label: 'Wed' },
  { key: 4, label: 'Thu' },
  { key: 5, label: 'Fri' },
  { key: 6, label: 'Sat' },
  { key: 7, label: 'Sun' },
];

const STATUS_CONFIG: Record<string, { label: string; borderColor: string; textColor: string }> = {
  usual: {
    label: strings.statusLabels.usual,
    borderColor: 'var(--border)',
    textColor: 'var(--text-2)',
  },
  today: {
    label: strings.statusLabels.today,
    borderColor: 'var(--amber)',
    textColor: 'var(--amber)',
  },
  changed: {
    label: strings.statusLabels.changed,
    borderColor: 'var(--amber)',
    textColor: 'var(--amber)',
  },
  cancelled: {
    label: strings.statusLabels.cancelled,
    borderColor: 'var(--text-2)',
    textColor: 'var(--text-2)',
  },
  extra: {
    label: strings.statusLabels.extra,
    borderColor: 'var(--mint)',
    textColor: 'var(--mint)',
  },
};

function formatDayMonth(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function localDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildUpcomingItems(
  rules: Rule[],
  exceptions: Exception[],
  _tz: string,
  weekStart: number,
  weekEnd: number,
  rangeEnd: number
): UpcomingItem[] {
  const items: UpcomingItem[] = [];
  const exceptionMap = new Map<string, Exception[]>();
  for (const exc of exceptions) {
    if (!exceptionMap.has(exc.local_date)) exceptionMap.set(exc.local_date, []);
    exceptionMap.get(exc.local_date)!.push(exc);
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const todayStr = localDateStr(new Date());

  const currentDate = new Date(weekStart);
  const endDate = new Date(rangeEnd);
  while (currentDate <= endDate) {
    const dateStr = localDateStr(currentDate);
    const weekday = new Date(dateStr + 'T12:00:00').getDay();
    const isoWeekday = weekday === 0 ? 7 : weekday;

    const dateExceptions = exceptionMap.get(dateStr) || [];
    const extra = dateExceptions.find((e) => e.type === 'extra' && e.rule_id === null);
    if (extra) {
      items.push({
        date: dateStr,
        day: dayNames[weekday],
        start_time: extra.start_time!,
        end_time: extra.end_time!,
        status: 'extra',
        ruleId: null,
        exceptionId: extra.id,
      });
    }

    // Usual/changed/cancelled occurrences only for the current week (Mon-Sun)
    if (currentDate.getTime() <= weekEnd) {
      for (const rule of rules) {
        if (!rule.weekdays.includes(isoWeekday)) continue;
        // The current week is the display unit: show every matching day this week.
        // Only honour an explicit end date (valid_to).
        if (rule.valid_to) {
          const toStr = localDateStr(new Date(rule.valid_to));
          if (dateStr > toStr) continue;
        }

        const cancelled = dateExceptions.find(
          (e) => e.type === 'cancelled' && e.rule_id === rule.id
        );
        const changed = dateExceptions.find((e) => e.type === 'changed' && e.rule_id === rule.id);

        if (cancelled) {
          items.push({
            date: dateStr,
            day: dayNames[weekday],
            start_time: rule.start_time,
            end_time: rule.end_time,
            status: 'cancelled',
            ruleId: rule.id,
            exceptionId: cancelled.id,
          });
        } else if (changed) {
          items.push({
            date: dateStr,
            day: dayNames[weekday],
            start_time: changed.start_time!,
            end_time: changed.end_time!,
            status: 'changed',
            tagText: dateStr === todayStr ? 'Today' : undefined,
            ruleId: rule.id,
            exceptionId: changed.id,
          });
        } else {
          items.push({
            date: dateStr,
            day: dayNames[weekday],
            start_time: rule.start_time,
            end_time: rule.end_time,
            status: dateStr === todayStr ? 'today' : 'usual',
            ruleId: rule.id,
            exceptionId: null,
          });
        }
      }
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  items.sort((a, b) => a.date.localeCompare(b.date));
  return items;
}

const timeInputStyle: React.CSSProperties = {
  minHeight: 44,
  padding: '0 12px',
  borderRadius: 10,
  border: '1px solid var(--border-strong)',
  background: 'var(--surface)',
  color: 'var(--text)',
  font: '500 15px var(--font-body)',
  colorScheme: 'dark',
  boxSizing: 'border-box',
  width: '100%',
};

const dayColStyle: React.CSSProperties = {
  width: 56,
  textAlign: 'center',
  font: '700 15px var(--font-heading)',
};

const dayColSpanStyle: React.CSSProperties = {
  display: 'block',
  font: '400 12px var(--font-body)',
  color: '#9AA1AF',
};

const pillStyle = (borderColor: string, textColor: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: '999px',
  font: '600 12px var(--font-body)',
  border: `1px solid ${borderColor}`,
  color: textColor,
});

export default function AddTimesPage() {
  const navigate = useNavigate();
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([1, 3, 5]);
  const [ruleStart, setRuleStart] = useState('20:00');
  const [ruleEnd, setRuleEnd] = useState('23:00');
  const [oneTimeDate, setOneTimeDate] = useState('');
  const [oneTimeStart, setOneTimeStart] = useState('20:00');
  const [oneTimeEnd, setOneTimeEnd] = useState('22:30');
  const [editingItem, setEditingItem] = useState<UpcomingItem | null>(null);
  const [applyTo, setApplyTo] = useState<'only' | 'future'>('only');
  const [stillTrueConfirmed, setStillTrueConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [upcomingItems, setUpcomingItems] = useState<UpcomingItem[]>([]);

  const computeWeekRange = (now: Date): { weekStart: number; weekEnd: number } => {
    const d = new Date(now);
    const day = d.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() + mondayOffset);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    return { weekStart: weekStart.getTime(), weekEnd: weekEnd.getTime() };
  };

  useEffect(() => {
    let cancelled = false;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();
    const today = new Date(now.toLocaleString('en-US', { timeZone: tz }));
    today.setHours(0, 0, 0, 0);
    const { weekStart, weekEnd } = computeWeekRange(today);
    const rangeEnd = new Date(today).setDate(today.getDate() + 14);

    Promise.all([
      fetch('/api/rules', { credentials: 'include' }).then((r) =>
        r.ok ? r.json() : { rules: [] }
      ),
      fetch('/api/exceptions', { credentials: 'include' }).then((r) =>
        r.ok ? r.json() : { exceptions: [] }
      ),
    ])
      .then(([rulesData, excData]) => {
        const rules: Rule[] = (rulesData.rules || []).map(
          (r: {
            id: number;
            user_id: string;
            weekdays: string | number[];
            start_time: string;
            end_time: string;
            tz: string;
            valid_from: number;
            valid_to: number | null;
          }) => ({
            id: r.id,
            user_id: r.user_id,
            weekdays:
              typeof r.weekdays === 'string' ? r.weekdays.split(',').map(Number) : r.weekdays,
            start_time: r.start_time,
            end_time: r.end_time,
            tz: r.tz,
            valid_from: r.valid_from,
            valid_to: r.valid_to,
          })
        );
        const exceptions: Exception[] = (excData.exceptions || []).map(
          (e: {
            id: number;
            user_id: string;
            rule_id: number | null;
            local_date: string;
            type: 'cancelled' | 'changed' | 'extra';
            start_time: string | null;
            end_time: string | null;
          }) => ({
            id: e.id,
            user_id: e.user_id,
            rule_id: e.rule_id,
            local_date: e.local_date,
            type: e.type,
            start_time: e.start_time,
            end_time: e.end_time,
          })
        );
        const items = buildUpcomingItems(rules, exceptions, tz, weekStart, weekEnd, rangeEnd);
        if (!cancelled) setUpcomingItems(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleWeekday = useCallback((day: number) => {
    setSelectedWeekdays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  }, []);

  const isWeekdaySelected = (day: number) => selectedWeekdays.includes(day);

  const handleSaveRule = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSaveError('');
    try {
      const response = await fetch('/api/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          weekdays: selectedWeekdays.join(','),
          start_time: ruleStart,
          end_time: ruleEnd,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          valid_from: 0,
          valid_to: null,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        setSaveError(data.error || 'Failed to save rule');
        return;
      }
      setSelectedWeekdays([1, 3, 5]);
      setRuleStart('20:00');
      setRuleEnd('23:00');
      await refreshUpcoming();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOneTime = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oneTimeDate) {
      alert('Please pick a date');
      return;
    }
    setLoading(true);
    setSaveError('');
    try {
      const response = await fetch('/api/exceptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          rule_id: null,
          local_date: oneTimeDate,
          type: 'extra',
          start_time: oneTimeStart,
          end_time: oneTimeEnd,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        setSaveError(data.error || 'Failed to add slot');
        return;
      }
      setOneTimeDate('');
      setOneTimeStart('20:00');
      setOneTimeEnd('22:30');
      await refreshUpcoming();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to add slot');
    } finally {
      setLoading(false);
    }
  };

  const refreshUpcoming = async () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const { weekStart, weekEnd } = computeWeekRange(today);
    const rangeEnd = new Date(today).getTime() + 14 * 24 * 60 * 60 * 1000;
    const [rulesData, excData] = await Promise.all([
      fetch('/api/rules', { credentials: 'include' }).then((r) => r.json()),
      fetch('/api/exceptions', { credentials: 'include' }).then((r) => r.json()),
    ]);
    const rules: Rule[] = (rulesData.rules || []).map(
      (r: {
        id: number;
        user_id: string;
        weekdays: string | number[];
        start_time: string;
        end_time: string;
        tz: string;
        valid_from: number;
        valid_to: number | null;
      }) => ({
        id: r.id,
        user_id: r.user_id,
        weekdays: typeof r.weekdays === 'string' ? r.weekdays.split(',').map(Number) : r.weekdays,
        start_time: r.start_time,
        end_time: r.end_time,
        tz: r.tz,
        valid_from: r.valid_from,
        valid_to: r.valid_to,
      })
    );
    const exceptions: Exception[] = (excData.exceptions || []).map(
      (e: {
        id: number;
        user_id: string;
        rule_id: number | null;
        local_date: string;
        type: 'cancelled' | 'changed' | 'extra';
        start_time: string | null;
        end_time: string | null;
      }) => ({
        id: e.id,
        user_id: e.user_id,
        rule_id: e.rule_id,
        local_date: e.local_date,
        type: e.type,
        start_time: e.start_time,
        end_time: e.end_time,
      })
    );
    setUpcomingItems(buildUpcomingItems(rules, exceptions, tz, weekStart, weekEnd, rangeEnd));
  };

  const handleCancelItem = async (item: UpcomingItem) => {
    if (!window.confirm(`Cancel ${item.day} ${formatDayMonth(item.date)}?`)) return;
    setLoading(true);
    setSaveError('');
    try {
      const exceptionId = item.exceptionId;
      const url = exceptionId ? `/api/exceptions/${exceptionId}` : `/api/exceptions`;
      const method = exceptionId ? 'DELETE' : 'POST';
      const body = exceptionId
        ? undefined
        : JSON.stringify({
            rule_id: item.ruleId ?? null,
            local_date: item.date,
            type: 'cancelled',
            start_time: null,
            end_time: null,
          });
      const response = await fetch(url, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        credentials: 'include',
        body,
      });
      if (!response.ok) {
        const data = await response.json();
        setSaveError(data.error || 'Failed to cancel');
        return;
      }
      setEditingItem(null);
      await refreshUpcoming();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    setLoading(true);
    setSaveError('');
    try {
      const exceptionId = editingItem.exceptionId;
      const url = exceptionId ? `/api/exceptions/${exceptionId}` : `/api/exceptions`;
      const method = exceptionId ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          rule_id: editingItem.ruleId ?? null,
          local_date: editingItem.date,
          type: editingItem.status === 'cancelled' ? 'cancelled' : 'changed',
          start_time: ruleStart,
          end_time: ruleEnd,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        setSaveError(data.error || 'Failed to save change');
        return;
      }
      setEditingItem(null);
      setApplyTo('only');
      await refreshUpcoming();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllRules = async () => {
    if (!window.confirm('Delete all your usual times? This cannot be undone.')) return;
    setLoading(true);
    setSaveError('');
    try {
      const response = await fetch('/api/rules', {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json();
        setSaveError(data.error || 'Failed to clear usual times');
        return;
      }
      setSelectedWeekdays([1, 3, 5]);
      setRuleStart('20:00');
      setRuleEnd('23:00');
      await refreshUpcoming();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to clear usual times');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToUsual = () => {
    setEditingItem(null);
    setApplyTo('only');
  };

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        color: 'var(--text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header right={<AccountMenu />} />

      <main
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          padding: '24px 32px',
          width: '100%',
          maxWidth: 1360,
          margin: '0 auto',
          boxSizing: 'border-box',
          flex: 1,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <button
            type="button"
            onClick={() => navigate('/main')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              minHeight: 44,
              padding: '0 14px',
              borderRadius: 10,
              border: '1px solid var(--border-strong)',
              background: 'var(--surface)',
              color: 'var(--text)',
              font: '500 14px var(--font-body)',
              cursor: 'pointer',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 6l-6 6 6 6" />
            </svg>
            Calendar
          </button>
          <div>
            <h1 style={{ margin: 0, font: '700 28px var(--font-heading)' }}>
              Add my <span style={{ color: '#2EE6A6' }}>times</span>
            </h1>
            <p style={{ margin: '4px 0 0', font: '400 14px var(--font-body)', color: '#9AA1AF' }}>
              Set your usual evenings once. Change a single day any time, without touching the rest.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 24 }}>
          <div
            style={{
              flex: '1 1 360px',
              maxWidth: 460,
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
            }}
          >
            <section
              style={{
                background: 'var(--bg-bar)',
                border: '1px solid var(--border)',
                borderRadius: 16,
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <div>
                <h2 style={{ margin: 0, font: '700 18px var(--font-heading)' }}>Usual times</h2>
                <p
                  style={{ margin: '4px 0 0', font: '400 14px var(--font-body)', color: '#9AA1AF' }}
                >
                  Repeats every week until you change it.
                </p>
              </div>
              <div
                style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                role="group"
                aria-label="Days"
              >
                {WEEKDAYS.map((wd) => (
                  <button
                    key={wd.key}
                    type="button"
                    onClick={() => toggleWeekday(wd.key)}
                    aria-pressed={isWeekdaySelected(wd.key)}
                    style={{
                      minWidth: 52,
                      minHeight: 44,
                      padding: '0 10',
                      borderRadius: 10,
                      border: '1px solid var(--border-strong)',
                      background: isWeekdaySelected(wd.key) ? '#2EE6A6' : 'transparent',
                      color: isWeekdaySelected(wd.key) ? '#04120C' : '#C4C9D4',
                      font: '600 14px var(--font-body)',
                      cursor: 'pointer',
                    }}
                  >
                    {wd.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    font: '500 13px var(--font-body)',
                    color: '#9AA1AF',
                  }}
                >
                  From
                  <input
                    type="time"
                    value={ruleStart}
                    onChange={(e) => setRuleStart(e.target.value)}
                    style={timeInputStyle}
                  />
                </label>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    font: '500 13px var(--font-body)',
                    color: '#9AA1AF',
                  }}
                >
                  To
                  <input
                    type="time"
                    value={ruleEnd}
                    onChange={(e) => setRuleEnd(e.target.value)}
                    style={timeInputStyle}
                  />
                </label>
              </div>
              <p style={{ margin: 0, font: '400 14px var(--font-body)', color: '#9AA1AF' }}>
                Times are in your timezone: {timezone}.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <button
                  type="button"
                  onClick={handleSaveRule}
                  disabled={loading}
                  style={{
                    minHeight: 44,
                    padding: '0 14px',
                    borderRadius: 10,
                    border: '1px solid #FFB547',
                    background: '#FFB547',
                    color: '#1A1203',
                    font: '700 14px var(--font-body)',
                    cursor: 'pointer',
                  }}
                >
                  {loading ? 'Saving...' : 'Save usual times'}
                </button>
                <button
                  type="button"
                  style={{
                    minHeight: 44,
                    padding: '0 14px',
                    borderRadius: 10,
                    border: '1px solid var(--border-strong)',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    font: '500 14px var(--font-body)',
                    cursor: 'pointer',
                  }}
                >
                  Add another time range
                </button>
                <button
                  type="button"
                  onClick={handleClearAllRules}
                  disabled={loading}
                  style={{
                    minHeight: 44,
                    padding: '0 14px',
                    borderRadius: 10,
                    border: '1px solid var(--border-strong)',
                    background: 'transparent',
                    color: '#FF5C5C',
                    font: '500 14px var(--font-body)',
                    cursor: 'pointer',
                  }}
                >
                  Clear all usual times
                </button>
              </div>
              {saveError && (
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    background: '#3A1A1A',
                    border: '1px solid #FF5C5C',
                    color: '#FFB3B3',
                    font: '500 14px var(--font-body)',
                  }}
                >
                  {saveError}
                </div>
              )}
            </section>

            <section
              style={{
                background: 'var(--bg-bar)',
                border: '1px solid var(--border)',
                borderRadius: 16,
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div>
                <h2 style={{ margin: 0, font: '700 18px var(--font-heading)' }}>
                  Extra one-time slot
                </h2>
                <p
                  style={{ margin: '4px 0 0', font: '400 14px var(--font-body)', color: '#9AA1AF' }}
                >
                  For the odd evening that isn't in your usual times.
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    font: '500 13px var(--font-body)',
                    color: '#9AA1AF',
                    flex: 1,
                  }}
                >
                  Date
                  <input
                    type="date"
                    value={oneTimeDate}
                    onChange={(e) => setOneTimeDate(e.target.value)}
                    style={timeInputStyle}
                  />
                </label>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    font: '500 13px var(--font-body)',
                    color: '#9AA1AF',
                  }}
                >
                  From
                  <input
                    type="time"
                    value={oneTimeStart}
                    onChange={(e) => setOneTimeStart(e.target.value)}
                    style={timeInputStyle}
                  />
                </label>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    font: '500 13px var(--font-body)',
                    color: '#9AA1AF',
                  }}
                >
                  To
                  <input
                    type="time"
                    value={oneTimeEnd}
                    onChange={(e) => setOneTimeEnd(e.target.value)}
                    style={timeInputStyle}
                  />
                </label>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <button
                  type="button"
                  onClick={handleAddOneTime}
                  disabled={loading}
                  style={{
                    minHeight: 44,
                    padding: '0 14px',
                    borderRadius: 10,
                    border: '1px solid var(--border-strong)',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    font: '500 14px var(--font-body)',
                    cursor: 'pointer',
                  }}
                >
                  {loading ? 'Saving...' : 'Add one-time slot'}
                </button>
              </div>
              {saveError && (
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    background: '#3A1A1A',
                    border: '1px solid #FF5C5C',
                    color: '#FFB3B3',
                    font: '500 14px var(--font-body)',
                  }}
                >
                  {saveError}
                </div>
              )}
            </section>
          </div>

          <section
            style={{
              flex: '1.5 1 460px',
              minWidth: 0,
              background: 'var(--bg-bar)',
              border: '1px solid var(--border)',
              borderRadius: 16,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <div>
                <h2 style={{ margin: 0, font: '700 18px var(--font-heading)' }}>
                  Your upcoming times
                </h2>
                <p
                  style={{ margin: '4px 0 0', font: '400 14px var(--font-body)', color: '#9AA1AF' }}
                >
                  Your usual times for this week, plus every one-time slot you've added.
                </p>
              </div>
              <span style={{ font: '500 13px var(--font-body)', color: '#2EE6A6' }}>
                Friends can see these
              </span>
            </div>

            {stillTrueConfirmed ? (
              <p
                style={{
                  font: '400 13px var(--font-body)',
                  color: '#9AA1AF',
                  textAlign: 'center',
                  marginTop: 8,
                }}
              >
                Thanks for confirming — your times are still accurate this week.
              </p>
            ) : (
              <button
                type="button"
                onClick={() => setStillTrueConfirmed(true)}
                style={{
                  minHeight: 44,
                  padding: '0 14px',
                  borderRadius: 10,
                  border: '1px solid var(--border-strong)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  font: '500 14px var(--font-body)',
                  cursor: 'pointer',
                  alignSelf: 'flex-start',
                }}
              >
                Still true this week?
              </button>
            )}

            {upcomingItems.length === 0 ? (
              <p
                style={{
                  font: '400 13px var(--font-body)',
                  color: '#9AA1AF',
                  textAlign: 'center',
                  marginTop: 8,
                }}
              >
                No upcoming times yet. Add a usual time or one-time slot above.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {upcomingItems.map((item) => {
                  const status = STATUS_CONFIG[item.status];
                  const isEditing = editingItem?.date === item.date;
                  if (isEditing) {
                    return (
                      <div
                        key={item.date}
                        style={{
                          border: '1px solid #FFB547',
                          borderRadius: 12,
                          background: 'var(--surface)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '8px 10px',
                            borderRadius: 12,
                            background: 'transparent',
                          }}
                        >
                          <div style={{ ...dayColStyle, ...dayColSpanStyle }}>
                            {item.day}
                            <span style={dayColSpanStyle}>{formatDayMonth(item.date)}</span>
                          </div>
                          <div style={{ flex: '1 1 160px' }}>
                            <div style={{ font: '600 15px var(--font-body)', marginBottom: 4 }}>
                              {item.start_time} – {item.end_time}
                            </div>
                            <span style={pillStyle('#FFB547', '#FFB547')}>
                              {item.status === 'extra'
                                ? 'One-time slot'
                                : `Changed · ${item.start_time} – ${item.end_time}`}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditingItem(null)}
                            style={{
                              minHeight: 44,
                              padding: '0 16px',
                              border: 0,
                              background: 'transparent',
                              color: '#9AA1AF',
                              font: '500 14px var(--font-body)',
                              cursor: 'pointer',
                            }}
                          >
                            Close
                          </button>
                        </div>
                        <div
                          style={{
                            padding: 12,
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 14,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              gap: 4,
                              border: '1px solid var(--border-strong)',
                              borderRadius: 10,
                              overflow: 'hidden',
                              alignSelf: 'flex-start',
                              flexWrap: 'wrap',
                            }}
                            role="group"
                            aria-label="Apply change to"
                          >
                            <button
                              type="button"
                              onClick={() => setApplyTo('only')}
                              aria-pressed={applyTo === 'only'}
                              style={{
                                minHeight: 44,
                                padding: '0 16px',
                                border: 0,
                                background: applyTo === 'only' ? 'var(--border)' : 'var(--surface)',
                                color: applyTo === 'only' ? 'var(--text)' : '#9AA1AF',
                                font:
                                  applyTo === 'only'
                                    ? '600 14px var(--font-body)'
                                    : '500 14px var(--font-body)',
                                cursor: 'pointer',
                              }}
                            >
                              Only this time
                            </button>
                            <button
                              type="button"
                              onClick={() => setApplyTo('future')}
                              aria-pressed={applyTo === 'future'}
                              style={{
                                minHeight: 44,
                                padding: '0 16px',
                                border: 0,
                                background: 'var(--surface)',
                                color: applyTo === 'future' ? 'var(--text)' : '#9AA1AF',
                                font: '500 14px var(--font-body)',
                                cursor: 'pointer',
                              }}
                            >
                              This and all future times
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            <label
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                font: '500 13px var(--font-body)',
                                color: '#9AA1AF',
                              }}
                            >
                              From
                              <input
                                type="time"
                                value={ruleStart}
                                onChange={(e) => setRuleStart(e.target.value)}
                                style={timeInputStyle}
                              />
                            </label>
                            <label
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                font: '500 13px var(--font-body)',
                                color: '#9AA1AF',
                              }}
                            >
                              To
                              <input
                                type="time"
                                value={ruleEnd}
                                onChange={(e) => setRuleEnd(e.target.value)}
                                style={timeInputStyle}
                              />
                            </label>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            <button
                              type="button"
                              onClick={handleSaveChange}
                              disabled={loading}
                              style={{
                                minHeight: 44,
                                padding: '0 14px',
                                borderRadius: 10,
                                border: '1px solid #FFB547',
                                background: '#FFB547',
                                color: '#1A1203',
                                font: '700 14px var(--font-body)',
                                cursor: 'pointer',
                              }}
                            >
                              {loading ? 'Saving...' : 'Save change'}
                            </button>
                            <button
                              type="button"
                              onClick={handleResetToUsual}
                              style={{
                                minHeight: 44,
                                padding: '0 14px',
                                borderRadius: 10,
                                border: '1px solid var(--border-strong)',
                                background: 'transparent',
                                color: '#9AA1AF',
                                font: '500 14px var(--font-body)',
                                cursor: 'pointer',
                              }}
                            >
                              Reset to usual
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCancelItem(item)}
                              style={{
                                minHeight: 44,
                                padding: '0 14px',
                                borderRadius: 10,
                                border: '1px solid var(--border-strong)',
                                background: 'transparent',
                                color: '#9AA1AF',
                                font: '500 14px var(--font-body)',
                                cursor: 'pointer',
                              }}
                            >
                              Cancel this time
                            </button>
                          </div>
                          {saveError && (
                            <div
                              style={{
                                padding: '8px 12px',
                                borderRadius: 10,
                                background: '#3A1A1A',
                                border: '1px solid #FF5C5C',
                                color: '#FFB3B3',
                                font: '500 14px var(--font-body)',
                              }}
                            >
                              {saveError}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={item.date}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 10px',
                        borderRadius: 12,
                        background: 'var(--surface)',
                        opacity: item.status === 'cancelled' ? 0.8 : 1,
                      }}
                    >
                      <div style={{ ...dayColStyle }}>
                        {item.day}
                        <span style={dayColSpanStyle}>{formatDayMonth(item.date)}</span>
                      </div>
                      <div style={{ flex: '1 1 160px' }}>
                        <div
                          style={{
                            font: '600 15px var(--font-body)',
                            marginBottom: 4,
                            color: item.status === 'cancelled' ? '#9AA1AF' : 'var(--text)',
                            textDecoration: item.status === 'cancelled' ? 'line-through' : 'none',
                          }}
                        >
                          {item.start_time} – {item.end_time}
                        </div>
                        <span
                          style={{
                            ...pillStyle(status.borderColor, status.textColor),
                            marginRight: 6,
                          }}
                        >
                          {status.label}
                        </span>
                        {item.tagText && (
                          <span style={pillStyle(status.borderColor, status.textColor)}>
                            {item.tagText}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingItem(item);
                          setSaveError('');
                        }}
                        style={{
                          minHeight: 44,
                          padding: '0 16px',
                          border: 0,
                          background: 'transparent',
                          color: '#9AA1AF',
                          font: '500 14px var(--font-body)',
                          cursor: 'pointer',
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCancelItem(item)}
                        style={{
                          minHeight: 44,
                          padding: '0 16px',
                          border: 0,
                          background: 'transparent',
                          color: '#9AA1AF',
                          font: '500 14px var(--font-body)',
                          cursor: 'pointer',
                        }}
                      >
                        {item.status === 'cancelled'
                          ? 'Restore'
                          : item.status === 'extra'
                            ? 'Remove'
                            : 'Cancel'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <p style={{ margin: 0, font: '400 13px var(--font-body)', color: '#9AA1AF' }}>
              Changing or cancelling one day never touches the rest of your usual times.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
